import { Component, input, OnDestroy, OnInit, output, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DamageReason } from '@app/sales/modules/mobilephone/models/equipment-api.model';
import { MobilBytteSelectors } from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.selectors';
import { IceRadioButtonComponent } from '@app/shared/components/atoms/ice-radio-button/ice-radio-button.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { IceCheckboxComponent } from '@app/shared/components/molecules/ice-checkbox/ice-checkbox.component';
import { IceDatePickerComponent } from '@app/shared/components/molecules/ice-date-picker/ice-date-picker.component';
import { IceTextareaComponent } from '@app/shared/components/molecules/ice-textarea/ice-textarea.component';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { IconNames } from '@app/shared/enums/icon-names';
import { select } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { InformationButtonComponent } from '../../../information-button/information-button.component';
import { MatIcon } from '@angular/material/icon';

export enum isPhoneDamagedOptions {
  YES = 'Ja',
  NO = 'Nei',
}

@Component({
  selector: 'app-condition-step',
  imports: [
    IceRadioButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    IceDatePickerComponent,
    IceTextareaComponent,
    IceCheckboxComponent,
    IceSelectBoxComponent,
    IceAlertComponent,
    InformationButtonComponent,
    MatIcon,
  ],
  templateUrl: './condition-step.component.html',
  styleUrl: './condition-step.component.scss',
})
export class ConditionStepComponent implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);
  data = inject<{
    equipmentUpgradeAgreementId: number;
  }>(MAT_DIALOG_DATA);

  isPhoneDamagedOptions = isPhoneDamagedOptions;
  IconNames = IconNames;

  existingEquipmentUpgradeAgreements = select(MobilBytteSelectors.existingEquipmentUpgradeAgreements);

  damageReasons = input.required<DamageReason[]>();

  phoneHasDamageInquired = output<boolean>();

  phoneHasDamageActive = false;

  subscription: Subscription;

  ngOnInit(): void {
    this.subscription = this.isPhoneDamaged.valueChanges.subscribe(value => {
      if (value === isPhoneDamagedOptions.YES) {
        this.damageReasonsFormArray.enable();
        this.dateOfDamage.enable();
        this.explanationOfDamage.enable();
        this.confirmationOfExternalDamage.enable();
      } else {
        this.damageReasonsFormArray.disable();
        this.dateOfDamage.disable();
        this.explanationOfDamage.disable();
        this.confirmationOfExternalDamage.disable();
      }
    });
  }

  get hasInsurance(): boolean {
    const equipmentUpgradeAgreementId = this.data?.equipmentUpgradeAgreementId;
    if (!equipmentUpgradeAgreementId) {
      return false;
    }

    return !!this.existingEquipmentUpgradeAgreements().find(
      agreement => +agreement?.agreementId === equipmentUpgradeAgreementId
    )?.insuranceServiceId;
  }

  get conditionForm(): FormGroup {
    return this.formGroupDirective.form;
  }

  get isPhoneDamaged(): FormControl {
    return this.conditionForm?.controls?.isPhoneDamaged as FormControl;
  }

  get damageReasonsFormArray(): FormArray {
    return this.conditionForm?.controls?.damageReasons as FormArray;
  }

  get showDamageReasonsRequiredError(): boolean {
    return this.damageReasonsFormArray.invalid && this.damageReasonsFormArray.touched;
  }

  get dateOfDamage(): FormControl {
    return this.conditionForm?.controls?.dateOfDamage as FormControl;
  }

  get explanationOfDamage(): FormControl {
    return this.conditionForm?.controls?.explanationOfDamage as FormControl;
  }

  get confirmationOfExternalDamage(): FormControl {
    return this.conditionForm?.controls?.confirmationOfExternalDamage as FormControl;
  }

  get today(): Date {
    return new Date();
  }

  get oneYearAgo(): Date {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date;
  }

  togglePhoneHasDamage(): void {
    this.phoneHasDamageActive = !this.phoneHasDamageActive;
    this.phoneHasDamageInquired.emit(this.phoneHasDamageActive);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
